import { useEffect, useState } from 'react';
import type { ShopDto, ShopListingDto } from '@bb/shared';
import { api, newKey, type ProgressNews } from '../net/api';
import type { AvatarLook } from '../art/avatar';
import { Sheet } from './Sheet';
import { Thumb } from './Thumb';

export function Shop({ shopKey, look, onClose, onChanged }: { shopKey: string; look: AvatarLook; onClose: () => void; onChanged: (news?: ProgressNews) => void }) {
  const [shop, setShop] = useState<ShopDto>();
  const [busy, setBusy] = useState<number>();
  const [message, setMessage] = useState<{ kind: 'ok' | 'err'; text: string }>();
  // One idempotency key per listing click, kept across retries of that same click.
  const [keys] = useState(() => new Map<number, string>());

  const load = () => api.shop(shopKey).then(setShop, (e: Error) => setMessage({ kind: 'err', text: e.message }));
  useEffect(() => {
    void load();
  }, [shopKey]);

  async function buy(l: ShopListingDto) {
    setBusy(l.listingId);
    setMessage(undefined);
    const key = keys.get(l.listingId) ?? newKey();
    keys.set(l.listingId, key);
    try {
      const r = await api.purchase(l.listingId, key);
      keys.delete(l.listingId);
      setMessage({ kind: 'ok', text: `${l.name} is in your backpack.` });
      onChanged((r as { progress?: ProgressNews }).progress);
      await load();
    } catch (e) {
      keys.delete(l.listingId);
      setMessage({ kind: 'err', text: (e as Error).message });
    } finally {
      setBusy(undefined);
    }
  }

  const coins = shop?.balances.COINS ?? 0;
  const balanceOf = (currency: string) => shop?.balances[currency] ?? 0;
  const unit = (currency: string) => (currency === 'PEARLS' ? 'pearls' : currency === 'CANDY' ? 'candy' : 'coins');
  return (
    <Sheet title={shop?.shop.name ?? 'Shop'} subtitle={
        shop ? (
          <>
            You have <strong className="coins">{coins.toLocaleString()} coins</strong>
            {shop.listings.some((l) => l.currency === 'CANDY') && <> · <strong className="candy">{balanceOf('CANDY').toLocaleString()} candy</strong></>} · level {shop.level}
          </>
        ) : 'Loading…'
      } onClose={onClose} wide>
      {message && <p className={message.kind === 'ok' ? 'note-ok' : 'form-error'} role="status">{message.text}</p>}
      <div className="item-grid">
        {shop?.listings.map((l) => {
          const locked = shop.level < l.minLevel || (l.membersOnly && !shop.member);
          const owned = l.owned > 0 && l.type !== 'fish';
          return (
            <article key={l.listingId} className={`item-card rarity-${l.rarity}`}>
              <Thumb item={l} look={look} />
              <h3>{l.name}</h3>
              <p className="muted small-print">{l.description}</p>
              <div className="item-foot">
                <span className={l.currency === 'PEARLS' ? 'price pearls' : l.currency === 'CANDY' ? 'price candy' : 'price'}>{l.price} {unit(l.currency)}</span>
                {owned ? (
                  <span className="tag">Owned</span>
                ) : locked ? (
                  <span className="tag">{l.membersOnly && !shop.member ? 'Members' : `Level ${l.minLevel}`}</span>
                ) : (
                  <button className="btn small" disabled={busy !== undefined || balanceOf(l.currency) < l.price} onClick={() => void buy(l)}>
                    {busy === l.listingId ? 'Buying…' : 'Buy'}
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </Sheet>
  );
}
